import { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Group,
  RemoveScroll,
  ScrollArea,
  Select,
  Stack
} from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import SubmitActionSplitButton from '../../components/SubmitActionSplitButton/SubmitActionSplitButton';
import { ICashierBox } from '../../interfaces/Store';
import { useGetStoreById } from '../../hooks/stores/queries';
import { tryPrintCashierBoxMovementReceipt } from '../../utils/cashierBoxMovementReceiptPrint';
import AddMovementForm from './AddMovementForm';
import { useCashierBoxMovementForm } from './useCashierBoxMovementForm';
import classes from './CashierBoxMovementDrawer.module.css';

type CashierBoxMovementDrawerProps = {
  opened: boolean;
  onClose: () => void;
  storeId: string;
  cashierBoxes: ICashierBox[];
};

const getInitialCashierBoxId = (cashierBoxes: ICashierBox[]) =>
  cashierBoxes.length === 1 ? cashierBoxes[0]?._id || '' : '';

const CashierBoxMovementDrawer = ({
  opened,
  onClose,
  storeId,
  cashierBoxes
}: CashierBoxMovementDrawerProps) => {
  const { t: tCommon } = useTranslation('CommonButtons');
  const { t: tFieldName } = useTranslation('FieldName');
  const { t: tCashierboxes } = useTranslation('CashierboxesPage');
  const [selectedCashierBoxId, setSelectedCashierBoxId] = useState(
    getInitialCashierBoxId(cashierBoxes)
  );
  const hasMultipleCashierBoxes = cashierBoxes.length > 1;
  const {
    data: store,
    isLoading: isStoreLoading,
    isFetching: isStoreFetching,
    isError: isStoreError,
  } = useGetStoreById(!!storeId, storeId);
  const selectedCashierBox =
    cashierBoxes.find((cashierBox) => cashierBox._id === selectedCashierBoxId) || null;
  const canPrintMovementReceipt =
    !!selectedCashierBoxId &&
    !!selectedCashierBox &&
    !!store &&
    !isStoreLoading &&
    !isStoreFetching &&
    !isStoreError;

  const resetSelectedCashierBox = () => {
    setSelectedCashierBoxId(getInitialCashierBoxId(cashierBoxes));
  };

  const { form, submit, resetForm, isSubmitting } = useCashierBoxMovementForm({
    storeId,
    cashierBoxId: selectedCashierBoxId,
    onSuccess: () => {
      resetSelectedCashierBox();
      onClose();
    }
  });

  useEffect(() => {
    if (opened) {
      resetSelectedCashierBox();
      resetForm();
    }
  }, [opened, cashierBoxes]);

  const handleCancel = () => {
    resetForm();
    resetSelectedCashierBox();
    onClose();
  };

  const handleSubmit = form.onSubmit((values) => {
    if (!selectedCashierBoxId) return;
    return submit(values);
  });

  const handleSubmitAndPrint = form.onSubmit((values) => {
    if (!canPrintMovementReceipt) return null;

    return submit(values, {
      afterSave: async (movement) => {
        await tryPrintCashierBoxMovementReceipt({
          movement,
          cashierBox: selectedCashierBox,
          store,
          t: tCashierboxes,
          failureTitle: tCashierboxes('movementReceipt.savedButPrintFailed'),
        });
      },
    });
  });

  const getPrintDisabledReason = () => {
    if (isSubmitting || canPrintMovementReceipt) return undefined;
    if (isStoreLoading || isStoreFetching) {
      return tCashierboxes('movementReceipt.storeLoadingPrintDisabled');
    }
    if (!store || isStoreError) {
      return tCashierboxes('movementReceipt.storeUnavailablePrintDisabled');
    }
    if (!selectedCashierBoxId || !selectedCashierBox) {
      return tCashierboxes('movementReceipt.cashierBoxUnavailablePrintDisabled');
    }

    return undefined;
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleCancel}
      position="right"
      size="md"
      className={RemoveScroll.classNames.fullWidth}
      title={tCommon('addCashierBoxMovement')}
      scrollAreaComponent={ScrollArea.Autosize}>
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {hasMultipleCashierBoxes && (
            <Select
              label={tFieldName('cashierBox')}
              placeholder={tFieldName('cashierBox')}
              allowDeselect={false}
              value={selectedCashierBoxId || null}
              onChange={(value) => setSelectedCashierBoxId(value || '')}
              data={cashierBoxes.map((cashierBox) => ({
                value: cashierBox._id || '',
                label: cashierBox.name
              }))}
            />
          )}
          <AddMovementForm form={form} />
          <Group className={classes.actions} justify="flex-end" gap="sm">
            <Button
              className={classes.cancelButton}
              variant="subtle"
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}>
              {tCommon('cancel')}
            </Button>
            <div className={classes.splitAction}>
              <SubmitActionSplitButton
                submitLabel={tCommon('save')}
                submitDisabled={!selectedCashierBoxId || isSubmitting}
                loading={isSubmitting}
                menuAriaLabel={tCommon('saveOptions')}
                actions={[
                  {
                    key: 'save-and-print',
                    label: tCommon('saveAndPrint'),
                    icon: <IconPrinter size={16} />,
                    onClick: () => void handleSubmitAndPrint(),
                    disabled: isSubmitting || !canPrintMovementReceipt,
                    disabledReason: getPrintDisabledReason(),
                  },
                ]}
              />
            </div>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
};

export default CashierBoxMovementDrawer;
